/** @jsxImportSource @emotion/react */
import { Drawer } from "../../components/Drawer";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as authSelectors from "../../module/auth/authSelectors";
import { AutoAddedProductMutationPayload } from "../../service/api-response/AutoAddedProducts";
import { ButtonAction } from "../../components/NewButtons";
import { useCreateAutoAddedProduct } from "../../service/api";
import { getErrorMessage } from "../../error-handler";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { ActionSpinner } from "../../components/ActionSpinner";
import { SelectProductInput } from "../../components/forms/inputs/SelectProductInput";
import { SwitchInput } from "../../components/forms/inputs/SwitchInput";
import { css } from "@emotion/react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const bodyStyling = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

type FormValues = AutoAddedProductMutationPayload & {
  triggerProductEnabled: boolean;
};

const schema = yup.object().shape({
  productId: yup.string().required("Product is required"),
  triggerProductEnabled: yup.boolean(),
  triggerProductId: yup.string().when("triggerProductEnabled", {
    is: true,
    then: (schema) => schema.required("Select a trigger product"),
    otherwise: (schema) => schema.optional(),
  }),
});

export const AddAutoAddedProductDrawer = ({ isOpen, onClose }: Props) => {
  const { shopId } = useParams();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const form = useForm<FormValues>({
    defaultValues: {
      amount: 1,
      locked: true,
      productId: "",
      triggerProductId: "",
      triggerProductEnabled: false,
    },
    resolver: yupResolver(schema),
  });
  const {
    mutateAsync: createAutoAddedProduct,
    isLoading: createAutoAddedProductLoading,
  } = useCreateAutoAddedProduct();

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (data: FormValues) => {
    const { triggerProductEnabled, ...rest } = data;
    if (!shopId) {
      return;
    }

    if (!triggerProductEnabled) {
      rest.triggerProductId = null;
    }

    try {
      await createAutoAddedProduct({
        data: {
          ...rest,
          shopId,
        },
        organizationKeyName,
      });

      toastr.success("Success", "Products added successfully");
      handleClose();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };
  const triggerProductEnabled = form.watch("triggerProductEnabled");

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      title="Add auto added product"
      width={500}
      buttons={
        <ButtonAction
          disabled={createAutoAddedProductLoading}
          onClick={form.handleSubmit(handleSubmit)}
        >
          {createAutoAddedProductLoading ? (
            <ActionSpinner />
          ) : (
            "Add auto added product"
          )}
        </ButtonAction>
      }
    >
      <FormProvider {...form}>
        <form css={bodyStyling}>
          <SelectProductInput
            name="productId"
            classification="generic"
            label="Product to add automatically to customer order"
          />
          <SwitchInput
            name="triggerProductEnabled"
            label="Only add product to customer order, if a trigger product is added as well."
          />
          {triggerProductEnabled && (
            <SelectProductInput
              name="triggerProductId"
              classification="admin-main-list"
              label="Trigger product"
              helperText="Select the product that will trigger adding the product to the user's order."
            />
          )}
        </form>
      </FormProvider>
    </Drawer>
  );
};
